import {LayoutType, MaskOptions} from '@wandb/weave/common/types/media';
import {RgbaColor, RgbColor} from '@wandb/weave/common/util/render_babylon';
import memoize from 'memoize-one';

import {RunSignature} from '../../types/run';
import {runHistoryRowsWithOneOfKeys} from '../../util/media';
import {
  AllBoundingBoxControls,
  AllMaskControls,
  MediaPanelCardControl,
  MoleculeConfig,
  RunWithHistoryAndMediaWandb,
} from '../MediaCard';

export const rowsWithKeysMemo = memoize(runHistoryRowsWithOneOfKeys);
export type Axis = 'step' | 'index' | 'run' | 'camera';
export type PanelModes = 'gallery' | 'grid';

export type PointColorSetting = {
  type: 'rgb';
  color: RgbColor;
};

export type KeyTypes = Partial<Record<string, string>>;

export type MediaSyncState = {
  currentTime: number;
  isPlaying: boolean;
  playbackRate: number;
  leaderId: string | null;
  lastUpdate: number;
};

export type MediaBrowserPanelConfig = {
  chartTitle?: string; //
  stepIndex?: number; // index of the step shown from each run, default: 0
  mediaIndex?: number; // index of the image shown from each run, default: 0
  mediaSliderKey?: string; // key for slider values, defaults to '_step'
  mediaKeys?: string[]; // key for media, like 'examples'

  actualSize?: boolean; //
  fitToDimension?: boolean; //
  pixelated?: boolean; //

  // Settings for tiling
  columnCount?: number; //
  mode?: PanelModes; //
  gallerySettings?: {
    axis: Axis;
  };
  gridSettings?: {
    xAxis: Axis;
    yAxis: Axis;
  };
  selection?: {
    xAxis?: [number, number];
    yAxis?: [number, number];
  };

  page?: {
    start?: number;
  };

  // How to stack masks
  tileLayout?: {
    [mediaKey: string]: {
      type: LayoutType;
      maskOptions: MaskOptions[];
    };
  };

  // The number of steps to increase on the up and down buttons of the step slider
  stepStrideLength?: number;
  snapToExistingStep?: boolean;
  maxGalleryItems?: number;
  maxRuns?: number;
  maxYAxisCount?: number;
  moleculeConfig?: MoleculeConfig;
  segmentationMaskConfig?: AllMaskControls;
  boundingBoxConfig?: AllBoundingBoxControls;
  sliderSync?: boolean;

  // Video synchronization settings
  videoPlaybackSyncEnabled?: boolean; // if sync is on at _any_ settings level
  videoPlaybackSync?: boolean; // if sync is on at _this_ settings level
  videoPlaybackSyncAutoplay?: boolean;
  videoPlaybackSyncLoop?: boolean;
  // seek synchronized videos to this time on first load
  videoPlaybackSyncCurrentTime?: number;

  // Object3D settings
  object3DBackgroundColor?: RgbaColor;
  pointColor?: PointColorSetting;
  useRightHandedSystem?: boolean;
};

export type NonEmptyTile = {
  mediaKey: string;
  mediaSliderKey?: string;
  mediaSliderKeyValue?: number;
  mediaStep: number;
  mediaIndex: number;
  runSignature: RunSignature;
  run: RunWithHistoryAndMediaWandb;
  controls?: MediaPanelCardControl;
  meta?: {
    camera?: {
      cameraIndex: number;
    };
  };

  // We don't set this for regular tiles, but it make the typing cleaner
  // to interact with empty tiles to have an optional here
  isEmpty?: false;
};
